<template>
  <div
    :class="[
      'header-area fixed top-0 z-[9999] transition-all duration-300 backdrop-blur-xl bg-white/95 dark:bg-[#0c1427]/95 border-b border-gray-200/50 dark:border-gray-700/50',
      { active: stateStoreInstance?.open },
    ]"
  >
    <div class="relative py-4 px-6">
      <div class="flex items-center justify-between">
        <!-- Left Section - Menu Button -->
        <div class="flex items-center">
          <button
            type="button"
            class="menu-toggle-btn w-11 h-11 flex items-center justify-center rounded-xl text-gray-600 dark:text-white hover:text-gray-700 dark:hover:text-white transition-all duration-300 shadow-md hover:shadow-lg backdrop-blur-sm hover:scale-105"
            @click="stateStoreInstance?.onChange"
          >
            <i class="ri-menu-line text-xl"></i>
          </button>
        </div>

        <!-- Right Section - Actions -->
        <div class="flex items-center gap-3">
          <!-- Notifications -->
          <div class="relative">
            <NotificationsLists />
          </div>

          <!-- Dark Mode Toggle -->
          <div class="relative">
            <DarkSwtichBtn />
          </div>

          <!-- Language Menu -->
          <div class="relative">
            <LanguageMenu />
          </div>

          <!-- Fullscreen Toggle -->
          <div class="relative hidden md:block">
            <ToggleFullscreenBtn />
          </div>

          <!-- Admin Profile -->
          <div class="relative">
            <AdminProfile />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, onBeforeUnmount } from "vue";
import stateStore from "@/utils/store";
import { useI18n } from "vue-i18n";
import emitter from "@/utils/emitter";

import DarkSwtichBtn from "./DarkSwtichBtn.vue";
import LanguageMenu from "./LanguageMenu.vue";
import ToggleFullscreenBtn from "./ToggleFullscreenBtn.vue";
import NotificationsLists from "./NotificationsLists.vue";
import AdminProfile from "./AdminProfile.vue";

export default defineComponent({
  name: "TopHeader",
  components: {
    DarkSwtichBtn,
    LanguageMenu,
    ToggleFullscreenBtn,
    NotificationsLists,
    AdminProfile,
  },
  setup() {
    const stateStoreInstance = stateStore;
    const { locale, messages } = useI18n();
    const isSticky = ref(false);
    const showDebug = ref(false);

    // Extract the translations for the current locale
    const translations = computed(() => {
      const currentMessages = messages.value[locale.value];
      return currentMessages || null;
    });

    const debugReload = () => {
      localStorage.setItem("reload_translations", "true");
      window.location.reload();
    };

    // Handle when app becomes active after being in background
    const handleAppBecameActive = () => {
      console.log("TopHeader: App became active, refreshing state");
      // Touch the stateStore to ensure reactivity
      if (stateStoreInstance) {
        // Fix the TypeScript error - can't test void for truthiness
        if (typeof stateStoreInstance.refresh === "function") {
          stateStoreInstance.refresh();
        } else {
          console.log("No refresh method available");
        }
      }
    };

    onMounted(() => {
      window.addEventListener("scroll", () => {
        let scrollPos = window.scrollY;
        isSticky.value = scrollPos >= 100;
      });

      // Listen for app becoming active event
      emitter.on("app:active", handleAppBecameActive);
    });

    onBeforeUnmount(() => {
      // Remove emitter listener
      emitter.off("app:active", handleAppBecameActive);
    });

    return {
      isSticky,
      stateStoreInstance,
      locale,
      translations,
      showDebug,
      debugReload,
    };
  },
});
</script>

<style scoped>
/* Header buttons styling */
.menu-toggle-btn,
.action-btn,
.notifications-btn,
.dark-switch-btn,
.language-btn,
.fullscreen-btn,
.admin-profile-btn {
  background: white;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  color: #6b7280;
}

.dark .menu-toggle-btn,
.dark .action-btn,
.dark .notifications-btn,
.dark .dark-switch-btn,
.dark .language-btn,
.dark .fullscreen-btn,
.dark .admin-profile-btn {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.menu-toggle-btn:hover,
.action-btn:hover,
.notifications-btn:hover,
.dark-switch-btn:hover,
.language-btn:hover,
.fullscreen-btn:hover,
.admin-profile-btn:hover {
  background: #f9fafb;
  border-color: rgba(0, 0, 0, 0.2);
  transform: translateY(-1px) scale(1.05);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  color: #374151;
}

.dark .menu-toggle-btn:hover,
.dark .action-btn:hover,
.dark .notifications-btn:hover,
.dark .dark-switch-btn:hover,
.dark .language-btn:hover,
.dark .fullscreen-btn:hover,
.dark .admin-profile-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 25px rgba(255, 255, 255, 0.1);
}

/* Glass morphism effect for header */
.header-area {
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

/* Smooth animations */
.header-area * {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
